import { Trash2 } from 'lucide-react';
import { Button } from './ui/Button';
import { sortByDivisionName } from '../utils/sortDivisionNames';
import { highlightRowClass, isHighlightedClass, stickyCellBg } from '../utils/classHighlight';

const NUTRITION_ITEMS = ['egg', 'banana', 'milk'];

export const EMPTY_NUTRITION_FORM = {
  egg_boys: '',
  egg_girls: '',
  banana_boys: '',
  banana_girls: '',
  milk_boys: '',
  milk_girls: '',
};

function getConsolidateGroup(divisionName) {
  const name = String(divisionName ?? '').trim().toUpperCase();
  if (name.startsWith('LKG') || name.startsWith('UKG')) return 'KG';
  const gradeMatch = name.match(/^(\d{1,2})/);
  if (gradeMatch) return gradeMatch[1];
  return name || 'Other';
}

function compareConsolidateGroup(a, b) {
  if (a === 'KG' && b !== 'KG') return -1;
  if (b === 'KG' && a !== 'KG') return 1;
  const an = parseInt(a, 10);
  const bn = parseInt(b, 10);
  const aIsNum = !Number.isNaN(an);
  const bIsNum = !Number.isNaN(bn);
  if (aIsNum && bIsNum) return an - bn;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.localeCompare(b);
}

function itemHasData(cls, itemType) {
  return cls[`${itemType}_boys`] != null || cls[`${itemType}_girls`] != null;
}

export function buildNutritionDetailRowsFromReport(reportClasses, itemType, records = []) {
  const recordByClass = new Map();
  (records || [])
    .filter((r) => r.item_type === itemType)
    .forEach((r) => {
      recordByClass.set(String(r.class_id), r);
    });

  const rows = (reportClasses || [])
    .filter((cls) => itemHasData(cls, itemType))
    .map((cls) => {
      const record = recordByClass.get(String(cls.class_id));
      const boys = parseInt(record?.boys ?? cls[`${itemType}_boys`], 10) || 0;
      const girls = parseInt(record?.girls ?? cls[`${itemType}_girls`], 10) || 0;
      return {
        class_id: cls.class_id,
        division_name: cls.division_name,
        teacher_name: cls.teacher_name,
        boys,
        girls,
        total: boys + girls,
        id: record?.id ?? null,
        item_type: itemType,
        hasRecord: true,
      };
    });
  return sortByDivisionName(rows);
}

export function buildNutritionConsolidatedRows(detailRows) {
  const map = new Map();
  (detailRows || []).forEach((row) => {
    const group = getConsolidateGroup(row.division_name);
    const prev = map.get(group) || { group, boys: 0, girls: 0, total: 0 };
    prev.boys += parseInt(row.boys, 10) || 0;
    prev.girls += parseInt(row.girls, 10) || 0;
    prev.total += parseInt(row.total, 10) || 0;
    map.set(group, prev);
  });
  return Array.from(map.values()).sort((a, b) => compareConsolidateGroup(a.group, b.group));
}

export function getActiveNutritionItems(reportClasses) {
  return NUTRITION_ITEMS.filter((item) => (reportClasses || []).some((cls) => itemHasData(cls, item)));
}

export function NutritionDetailTable({
  rows,
  labels,
  onDelete,
  deleteLoading,
  showDelete = false,
  highlightClassIds = [],
}) {
  if (!rows?.length) return null;
  const footerBoys = rows.reduce((sum, r) => sum + r.boys, 0);
  const footerGirls = rows.reduce((sum, r) => sum + r.girls, 0);
  const footerTotal = footerBoys + footerGirls;

  return (
    <>
      <div className="space-y-2 sm:hidden">
        {rows.map((row) => {
          const highlighted = isHighlightedClass(row.class_id, highlightClassIds);
          return (
          <div
            key={`${row.class_id}-${row.item_type}-card`}
            className={`rounded-lg border border-border p-3 ${highlightRowClass(highlighted) || 'bg-surface/50'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-text">{row.division_name}</p>
              {showDelete && row.id ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onDelete(row.id)}
                  isLoading={deleteLoading}
                  aria-label={labels.delete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center text-sm">
              <div>
                <p className="text-xs text-muted">{labels.boys}</p>
                <p className="font-semibold tabular-nums text-text">{row.boys}</p>
              </div>
              <div>
                <p className="text-xs text-muted">{labels.girls}</p>
                <p className="font-semibold tabular-nums text-text">{row.girls}</p>
              </div>
              <div>
                <p className="text-xs text-muted">{labels.totalStudents}</p>
                <p className="font-semibold tabular-nums text-text">{row.total}</p>
              </div>
            </div>
          </div>
          );
        })}
        <div className="rounded-lg border border-primary/25 bg-primary-light/40 p-3">
          <p className="text-sm font-semibold text-text">{labels.grandTotal}</p>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <p className="text-xs text-muted">{labels.boys}</p>
              <p className="font-semibold tabular-nums">{footerBoys}</p>
            </div>
            <div>
              <p className="text-xs text-muted">{labels.girls}</p>
              <p className="font-semibold tabular-nums">{footerGirls}</p>
            </div>
            <div>
              <p className="text-xs text-muted">{labels.totalStudents}</p>
              <p className="font-semibold tabular-nums">{footerTotal}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden overflow-x-auto rounded-xl border border-border sm:block">
      <table className="w-full min-w-max text-sm">
        <thead>
          <tr className="border-b border-border bg-surface">
            <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-medium text-muted shadow-sticky">
              {labels.division}
            </th>
            <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
              {labels.boys}
            </th>
            <th className="px-3 py-2 text-center font-medium text-muted">{labels.girls}</th>
            <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
              {labels.totalStudents}
            </th>
            {showDelete && (
              <th className="border-l border-border px-3 py-2 text-center font-medium text-muted w-16">
                {labels.actions}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const highlighted = isHighlightedClass(row.class_id, highlightClassIds);
            return (
            <tr
              key={`${row.class_id}-${row.item_type}`}
              className={`border-b border-border hover:bg-surface/50 ${highlightRowClass(highlighted)}`}
            >
              <td
                className={`sticky left-0 z-10 px-3 py-2 font-medium text-text shadow-sticky ${stickyCellBg(highlighted)}`}
              >
                {row.division_name}
              </td>
              <td className="border-l border-border px-3 py-2 text-center tabular-nums">{row.boys}</td>
              <td className="px-3 py-2 text-center tabular-nums">{row.girls}</td>
              <td className="border-l border-border px-3 py-2 text-center font-medium tabular-nums">
                {row.total}
              </td>
              {showDelete && (
                <td className="border-l border-border px-3 py-2 text-center">
                  {row.id ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(row.id)}
                      isLoading={deleteLoading}
                      aria-label={labels.delete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <span className="text-xs text-muted">—</span>
                  )}
                </td>
              )}
            </tr>
            );
          })}
          <tr className="bg-primary-light/40 font-semibold">
            <td className="sticky left-0 z-10 bg-primary-light/40 px-3 py-2 text-text shadow-sticky">
              {labels.grandTotal}
            </td>
            <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerBoys}</td>
            <td className="px-3 py-2 text-center tabular-nums">{footerGirls}</td>
            <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerTotal}</td>
            {showDelete && <td className="border-l border-border px-3 py-2" />}
          </tr>
        </tbody>
      </table>
      </div>
    </>
  );
}

export function NutritionConsolidatedTable({ rows, labels }) {
  if (!rows?.length) return null;
  const footerBoys = rows.reduce((sum, r) => sum + r.boys, 0);
  const footerGirls = rows.reduce((sum, r) => sum + r.girls, 0);
  const footerTotal = footerBoys + footerGirls;

  return (
    <>
      <div className="space-y-2 sm:hidden">
        {rows.map((row) => (
          <div key={`${row.group}-card`} className="rounded-lg border border-border bg-surface/50 p-3">
            <p className="font-semibold text-text">{row.group}</p>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center text-sm">
              <div>
                <p className="text-xs text-muted">{labels.boys}</p>
                <p className="font-semibold tabular-nums text-text">{row.boys}</p>
              </div>
              <div>
                <p className="text-xs text-muted">{labels.girls}</p>
                <p className="font-semibold tabular-nums text-text">{row.girls}</p>
              </div>
              <div>
                <p className="text-xs text-muted">{labels.totalStudents}</p>
                <p className="font-semibold tabular-nums text-text">{row.total}</p>
              </div>
            </div>
          </div>
        ))}
        <div className="rounded-lg border border-primary/25 bg-primary-light/40 p-3">
          <p className="text-sm font-semibold text-text">{labels.grandTotal}</p>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <p className="text-xs text-muted">{labels.boys}</p>
              <p className="font-semibold tabular-nums">{footerBoys}</p>
            </div>
            <div>
              <p className="text-xs text-muted">{labels.girls}</p>
              <p className="font-semibold tabular-nums">{footerGirls}</p>
            </div>
            <div>
              <p className="text-xs text-muted">{labels.totalStudents}</p>
              <p className="font-semibold tabular-nums">{footerTotal}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden overflow-x-auto rounded-xl border border-border sm:block">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="border-b border-border bg-surface">
              <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-medium text-muted shadow-sticky">
                {labels.classNumber}
              </th>
              <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
                {labels.boys}
              </th>
              <th className="px-3 py-2 text-center font-medium text-muted">{labels.girls}</th>
              <th className="border-l border-border px-3 py-2 text-center font-medium text-muted">
                {labels.totalStudents}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.group} className="border-b border-border hover:bg-surface/50">
                <td className="sticky left-0 z-10 bg-card px-3 py-2 font-medium text-text shadow-sticky">
                  {row.group}
                </td>
                <td className="border-l border-border px-3 py-2 text-center tabular-nums">{row.boys}</td>
                <td className="px-3 py-2 text-center tabular-nums">{row.girls}</td>
                <td className="border-l border-border px-3 py-2 text-center tabular-nums">{row.total}</td>
              </tr>
            ))}
            <tr className="bg-primary-light/40 font-semibold">
              <td className="sticky left-0 z-10 bg-primary-light/40 px-3 py-2 text-text shadow-sticky">
                {labels.grandTotal}
              </td>
              <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerBoys}</td>
              <td className="px-3 py-2 text-center tabular-nums">{footerGirls}</td>
              <td className="border-l border-border px-3 py-2 text-center tabular-nums">{footerTotal}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

export { NUTRITION_ITEMS };
